import type { ApiErrorBody } from '@visualitics-toolkit/shared';

export const API_PREFIX = import.meta.env.VITE_API_PREFIX ?? '/api/v1';
export const AUTH_PREFIX = '/api/auth';

/** Erro tipado do wire — as telas leem `code` para acender o CTA certo (backup, allowlist…). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const raw = await response.text();
  const parsed: unknown = raw ? safeJson(raw) : null;

  if (!response.ok) {
    const body = (parsed ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(
      response.status,
      body.code ?? 'ERROR',
      body.message ?? `Falha na requisição (${response.status}).`,
      body.details,
    );
  }

  return parsed as T;
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> =>
    request<T>(`${API_PREFIX}${path}${toQuery(params)}`),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(`${API_PREFIX}${path}`, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  postForm: <T>(path: string, form: FormData): Promise<T> =>
    request<T>(`${API_PREFIX}${path}`, {
      method: 'POST',
      body: form,
    }),
  put: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(`${API_PREFIX}${path}`, {
      method: 'PUT',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(`${API_PREFIX}${path}`, {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  delete: <T>(path: string): Promise<T> => request<T>(`${API_PREFIX}${path}`, { method: 'DELETE' }),
  auth: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(`${AUTH_PREFIX}${path}`, {
      method: body ? 'POST' : 'GET',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  downloadUrl: (path: string): string => `${API_PREFIX}${path}`,
};

function toQuery(params?: Record<string, string | number | undefined>): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}
